import React, { useEffect, useMemo, useState, useCallback } from "react";

function isMobileLike() {
  if (typeof window === "undefined") return false;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  const small = window.matchMedia?.("(max-width: 900px)")?.matches;
  return !!(coarse || uaMobile || small);
}

function isPortrait() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia) {
    return window.matchMedia("(orientation: portrait)").matches;
  }
  return window.innerHeight >= window.innerWidth;
}

export default function MobileLandscapePrompt({ appSelector = "#root" }) {
  const [mobile, setMobile] = useState(isMobileLike());
  const [portrait, setPortrait] = useState(isPortrait());
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");

  // Keep prompt visibility in sync
  useEffect(() => {
    setShow(mobile && portrait);
  }, [mobile, portrait]);

  // Watch for changes
  useEffect(() => {
    const onResize = () => {
      setMobile(isMobileLike());
      setPortrait(isPortrait());
    };
    const onOrientation = () => {
      setPortrait(isPortrait());
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onOrientation);

    const mql = window.matchMedia?.("(orientation: portrait)");
    if (mql && mql.addEventListener) {
      mql.addEventListener("change", onResize);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientation);
      if (mql && mql.removeEventListener) {
        mql.removeEventListener("change", onResize);
      }
    };
  }, []);

  // CSS rotation fallback toggle
  const setForcedLandscape = useCallback((on) => {
    const host = document.querySelector(appSelector) || document.body;
    host.classList.toggle("force-landscape", !!on);
  }, [appSelector]);

  // Try to lock to landscape (Android Chrome)
  const tryLockLandscape = useCallback(async () => {
    setError("");
  
    // user gesture target
    const el = document.documentElement;
  
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
  
      // NOTE: use window.screen (or globalThis.screen) to avoid no-restricted-globals
      const scr = (typeof window !== "undefined" && window.screen);
  
      if (scr?.orientation?.lock) {
        await scr.orientation.lock("landscape");
        setShow(false);
        setForcedLandscape(false);
        return;
      }
  
      // Fallback
      setForcedLandscape(true);
      setShow(false);
    } catch (e) {
      setForcedLandscape(true);
      setShow(false);
      setError((e && e.message) || "Orientation lock not available.");
    }
  }, [setForcedLandscape]);
  

  // If user rotates to landscape naturally, remove fallback
  useEffect(() => {
    if (!portrait) {
      setForcedLandscape(false);
      setShow(false);
    }
  }, [portrait, setForcedLandscape]);

  if (!show) return null;

  return (
    <div style={backdropStyle}>
      <div style={cardStyle} role="dialog" aria-modal="true" aria-labelledby="landscape-title">
        <h2 id="landscape-title" style={{ margin: 0, fontSize: 18 }}>Best on desktop</h2>
        <p style={{ margin: "8px 0 0" }}>
          This app is designed for desktop. You can still use it on mobile in landscape mode.
        </p>
        <p style={{ margin: "8px 0 0" }}>
          Your screen will be locked in landscape mode, swipe down and to the left or right to exit.
        </p>

        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={primaryBtn} onClick={tryLockLandscape}>
            Enter landscape
          </button>
          <button
            style={ghostBtn}
            onClick={() => {
              // Dismiss without forcing
              setForcedLandscape(false);
              setShow(false);
            }}
          >
            Continue anyway
          </button>
        </div>

        {!!error && (
          <p style={{ color: "#f88", marginTop: 10 }}>
            {error} On iOS Safari, orientation lock is not supported. You may need to rotate your device manually.
          </p>
        )}
      </div>
    </div>
  );
}

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "grid",
  placeItems: "center",
  zIndex: 100000,
};

const cardStyle = {
  background: "#151515",
  color: "#fff",
  width: "min(92vw, 480px)",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
  lineHeight: 1.35,
};

const primaryBtn = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #444",
  background: "#2a2a2a",
  color: "#fff",
  cursor: "pointer",
};

const ghostBtn = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #444",
  background: "transparent",
  color: "#fff",
  cursor: "pointer",
};